/**
 * ReferenceImageList.js
 *
 * 参考图列表组件 - 在选择技能后显示在画布底部
 * 样式参考 MentionDropdown 的网格布局
 *
 * 显示逻辑：
 * 1. 用户在画布上点击技能后，显示一个按钮在底部
 * 2. 点击按钮展开参考图网格列表
 * 3. 点击参考图可以查看大图或取消选择
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, X, Check } from 'lucide-react';

export default function ReferenceImageList({ referenceImages = [], referenceLabel = '风格', onSelect, onClose }) {
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  if (!referenceImages || referenceImages.length === 0) {
    return null;
  }

  const toggleImage = (img) => {
    setSelectedImageIds(prev => {
      const exists = prev.includes(img.id);
      if (exists) {
        return prev.filter(id => id !== img.id);
      } else {
        return [...prev, img.id];
      }
    });
  };

  // 确认选择：将选中的图片对象传递给父组件
  const handleConfirm = () => {
    const selected = referenceImages.filter(img => selectedImageIds.includes(img.id));
    onSelect?.(selected);
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 600,
          maxHeight: '80vh',
          background: 'var(--modal-surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          color: 'var(--text-body)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>请选择{referenceLabel}参考图</div>
          </div>
          <X
            size={20}
            style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            onClick={onClose}
          />
        </div>

        {/* 参考图网格 */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 20,
          }}
          className="custom-scrollbar"
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
          }}>
            {referenceImages.map(img => {
              const isSelected = selectedImageIds.includes(img.id);
              return (
                <div
                  key={img.id}
                  onClick={() => toggleImage(img)}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: isSelected
                      ? '2px solid #8b5cf6'
                      : '2px solid transparent',
                    transition: 'all 0.15s',
                    background: 'var(--surface-subtle)',
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.border = '2px solid rgba(139,92,246,0.4)';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.border = '2px solid transparent';
                  }}
                >
                  {/* 正方形缩略图 */}
                  <div style={{
                    width: '100%',
                    paddingBottom: '100%',
                    position: 'relative',
                    background: '#000',
                  }}>
                    <img
                      src={img.thumbnailUrl}
                      alt={img.name}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.display = 'flex';
                        e.target.parentElement.style.alignItems = 'center';
                        e.target.parentElement.style.justifyContent = 'center';
                        e.target.parentElement.innerHTML = '<div style="color:#666;font-size:12px;">加载失败</div>';
                      }}
                    />
                  </div>

                  {/* 选中标记 */}
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#8b5cf6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}>
                      <Check size={16} color="#fff" />
                    </div>
                  )}

                  {/* 图片名称 */}
                  <div style={{
                    padding: '8px 10px',
                    fontSize: 12,
                    color: 'var(--text-body)',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    background: 'var(--modal-surface)',
                  }}>
                    {img.name || '未命名'}
                  </div>

                  {/* 提示词预览 */}
                  {img.prompt && (
                    <div style={{
                      padding: '0 10px 8px',
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      background: 'var(--modal-surface)',
                    }}>
                      {img.prompt}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 底部按钮 */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          gap: 12,
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--border-strong)',
              background: 'transparent',
              color: 'var(--text-body)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #8b5cf6',
              background: '#8b5cf6',
              color: '#fff',
              cursor: selectedImageIds.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: 13,
              opacity: selectedImageIds.length > 0 ? 1 : 0.5,
            }}
            disabled={selectedImageIds.length === 0}
          >
            确定
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
